import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import Integration, { SALESFORCE_OBJECT_TYPES } from "@/model/interface/Integration";
import IntegrationSyncToggle from "@/components/pages/integrations/IntegrationSyncToggle";
import {
  useCheckSalesforceRealtimeMutation,
  useGetSalesforceRealtimeStatusQuery,
  useUpdateIntegrationSettingsMutation,
  useUpdateIntegrationSyncSettingsMutation,
  useSyncIntegrationMutation,
} from "@/store/rest_api";
import { formatDateTime } from "@/util/date";
import toast from "react-hot-toast";

interface Props {
  integration: Integration;
}

export default function SalesforceSettings({ integration }: Props) {
  const [updateSettings] = useUpdateIntegrationSettingsMutation();
  const [updateSyncSettings] = useUpdateIntegrationSyncSettingsMutation();
  const [syncIntegration, { isLoading: isSyncing }] = useSyncIntegrationMutation();
  const isActive = integration.status?.toLowerCase() === "active";
  const { data: realtime } = useGetSalesforceRealtimeStatusQuery(undefined, { skip: !isActive });
  const [checkRealtime, { isLoading: isCheckingRealtime }] = useCheckSalesforceRealtimeMutation();
  const settings = integration.settings || {};

  const handleCheckRealtime = async () => {
    try {
      const status = await checkRealtime().unwrap();
      toast.success(status.installed ? "Package found" : "Package not installed yet");
    } catch {
      toast.error("Could not check the package");
    }
  };

  const toggleSetting = async (key: string, value: boolean) => {
    try {
      await updateSettings({ id: integration.id, settings: { [key]: value } }).unwrap();
    } catch {
      toast.error("Failed to update setting");
    }
  };

  const updateSetting = async (key: string, value: any) => {
    try {
      await updateSettings({ id: integration.id, settings: { [key]: value } }).unwrap();
    } catch {
      toast.error("Failed to update setting");
    }
  };

  const updateSyncDirection = async (value: string) => {
    try {
      await updateSyncSettings({
        id: integration.id,
        syncEnabled: integration.syncEnabled !== false,
        syncDirection: value,
      }).unwrap();
    } catch {
      toast.error("Failed to update sync direction");
    }
  };

  const handleSync = async () => {
    try {
      await syncIntegration(integration).unwrap();
      toast.success("Contact sync completed");
    } catch {
      toast.error("Sync failed");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Connection</CardTitle>
          <CardDescription>Salesforce connection status and sync</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <IntegrationSyncToggle integration={integration} />

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Status</p>
              <p className="text-sm text-muted-foreground">
                {integration.lastSyncAt
                  ? `Last synced ${new Date(integration.lastSyncAt).toLocaleString()}`
                  : "Never synced"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={integration.status === "active" ? "default" : "secondary"}>{integration.status}</Badge>
              <Button
                onClick={handleSync}
                disabled={isSyncing || integration.syncEnabled === false || integration.status !== "active"}
                size="sm"
              >
                {isSyncing ? "Syncing..." : "Sync Now"}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Sync Direction</p>
              <p className="text-sm text-muted-foreground">Controls whether contacts are pulled, pushed, or both</p>
            </div>
            <Select
              value={integration.syncDirection || "BIDIRECTIONAL"}
              onValueChange={(value) => updateSyncDirection(value)}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BIDIRECTIONAL">Bidirectional</SelectItem>
                <SelectItem value="PULL_ONLY">Pull Only</SelectItem>
                <SelectItem value="PUSH_ONLY">Push Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Auto Lead Creation</CardTitle>
          <CardDescription>
            Automatically create records in Salesforce when calls or messages involve unknown numbers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="autoLeadInbound" className="flex flex-col gap-1">
              <span>Inbound calls</span>
              <span className="font-normal text-muted-foreground text-xs">
                Create a Lead when an inbound call comes from an unknown number
              </span>
            </Label>
            <Switch
              id="autoLeadInbound"
              checked={!!settings.autoLeadOnInboundCall}
              onCheckedChange={(v) => toggleSetting("autoLeadOnInboundCall", v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="autoLeadOutbound" className="flex flex-col gap-1">
              <span>Outbound calls</span>
              <span className="font-normal text-muted-foreground text-xs">
                Create a Lead when an outbound call is made to an unknown number
              </span>
            </Label>
            <Switch
              id="autoLeadOutbound"
              checked={!!settings.autoLeadOnOutboundCall}
              onCheckedChange={(v) => toggleSetting("autoLeadOnOutboundCall", v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="autoLeadSms" className="flex flex-col gap-1">
              <span>Inbound SMS</span>
              <span className="font-normal text-muted-foreground text-xs">
                Create a Lead when an inbound SMS comes from an unknown number
              </span>
            </Label>
            <Switch
              id="autoLeadSms"
              checked={!!settings.autoLeadOnInboundSms}
              onCheckedChange={(v) => toggleSetting("autoLeadOnInboundSms", v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="flex flex-col gap-1">
              <span>Default object type</span>
              <span className="font-normal text-muted-foreground text-xs">
                What type of Salesforce record to create for unknown numbers
              </span>
            </Label>
            <Select
              value={settings.defaultSalesforceObjectType || "LEAD"}
              onValueChange={(v) => updateSetting("defaultSalesforceObjectType", v)}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LEAD">Lead</SelectItem>
                <SelectItem value="CONTACT">Contact</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activity Logging</CardTitle>
          <CardDescription>Configure how calls and SMS are logged to Salesforce Tasks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="linkTaskToOpportunity" className="flex flex-col gap-1">
              <span>Link Tasks to Opportunities</span>
              <span className="font-normal text-muted-foreground text-xs">
                When a call is linked to a deal with a Salesforce ID, set the Task's WhatId to the Opportunity
              </span>
            </Label>
            <Switch
              id="linkTaskToOpportunity"
              checked={!!settings.linkTaskToOpportunity}
              onCheckedChange={(checked) => toggleSetting("linkTaskToOpportunity", checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="flex flex-col gap-1">
              <span>Phone number format</span>
              <span className="font-normal text-muted-foreground text-xs">
                How phone numbers appear in Task descriptions
              </span>
            </Label>
            <Select
              value={(settings.phoneNumberFormat as string) || "international"}
              onValueChange={(value) => updateSetting("phoneNumberFormat", value)}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="international">International (+1 555 123 4567)</SelectItem>
                <SelectItem value="national">National ((555) 123-4567)</SelectItem>
                <SelectItem value="compact">Compact (5551234567)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Object Types to Sync</CardTitle>
          <CardDescription>Choose which Salesforce object types to sync during contact pull</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {SALESFORCE_OBJECT_TYPES.map((objectType) => {
            const enabled = settings.syncObjectTypes
              ? (settings.syncObjectTypes as string[]).includes(objectType)
              : objectType !== "ACCOUNT";
            return (
              <div key={objectType} className="flex items-center justify-between">
                <Label className="capitalize">{objectType.toLowerCase()}s</Label>
                <Switch
                  checked={enabled}
                  onCheckedChange={(checked) => {
                    const current = (settings.syncObjectTypes as string[]) || ["LEAD", "CONTACT"];
                    const updated = checked
                      ? [...current, objectType]
                      : current.filter((existing: string) => existing !== objectType);
                    updateSetting("syncObjectTypes", updated);
                  }}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contact Type Priority</CardTitle>
          <CardDescription>
            When a phone number matches multiple Salesforce object types, which type takes precedence
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(() => {
            const priority = (settings.contactTypePriority as string[]) || ["CONTACT", "ACCOUNT", "LEAD"];
            const moveUp = (index: number) => {
              if (index === 0) {
                return;
              }
              const updated = [...priority];
              [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
              updateSetting("contactTypePriority", updated);
            };
            const moveDown = (index: number) => {
              if (index === priority.length - 1) {
                return;
              }
              const updated = [...priority];
              [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
              updateSetting("contactTypePriority", updated);
            };
            return priority.map((type, index) => (
              <div key={type} className="flex items-center justify-between py-1">
                <span className="text-sm capitalize">{type.toLowerCase()}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" disabled={index === 0} onClick={() => moveUp(index)}>
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={index === priority.length - 1}
                    onClick={() => moveDown(index)}
                  >
                    ↓
                  </Button>
                </div>
              </div>
            ));
          })()}
          <p className="text-xs text-muted-foreground">
            Higher = takes precedence when the same phone matches multiple types
          </p>
        </CardContent>
      </Card>

      {isActive && (
        <Card>
          <CardHeader>
            <CardTitle>Real-time sync</CardTitle>
            <CardDescription>
              The Retreevo package in your Salesforce org sends changes the moment they happen. Without it, changes
              are picked up every minute.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-1">
                {realtime?.installed ? (
                  <>
                    <span className="status-pill-success">Installed</span>
                    <p className="text-sm text-muted-foreground">
                      {realtime.lastEventAt
                        ? `Last change received ${formatDateTime(realtime.lastEventAt)}`
                        : "No changes received yet"}
                    </p>
                  </>
                ) : (
                  <>
                    <span className="status-pill-neutral">Not installed</span>
                    <p className="text-sm text-muted-foreground">
                      {realtime?.installUrl
                        ? "Install the package in Salesforce, then check again."
                        : "The package is not available yet."}
                    </p>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!realtime?.installed && realtime?.installUrl && (
                  <Button size="sm" asChild>
                    <a href={realtime.installUrl} target="_blank" rel="noreferrer">
                      Install package
                    </a>
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleCheckRealtime} disabled={isCheckingRealtime}>
                  {isCheckingRealtime ? "Checking..." : "Check again"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!!settings.detailedLogging && (
      <Card>
        <CardHeader>
          <CardTitle>Detailed Logging</CardTitle>
          <CardDescription>
            Create structured Retreevo_Log__c records in Salesforce alongside Tasks. Requires the Retreevo custom object
            to be installed in your Salesforce org.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <Label htmlFor="detailedLogging" className="flex flex-col gap-1">
              <span>Enable detailed logging</span>
              <span className="font-normal text-muted-foreground text-xs">
                Creates a Retreevo_Log__c record with structured fields for each call and SMS
              </span>
            </Label>
            <Switch
              id="detailedLogging"
              checked={!!settings.detailedLogging}
              onCheckedChange={(checked) => toggleSetting("detailedLogging", checked)}
            />
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
